/**
 * Web Project List Page
 * 
 * Clean composition layer using the Facade pattern.
 * 
 * Architecture:
 * - Uses shared useProjectListPageFacade from @ideoz/core (zero duplication!)
 * - Injects web-specific implementations (navigation, auth)
 * - Passes organized facade to ProjectListPageTemplate
 * 
 * Benefits:
 * - Minimal code, maximum reusability
 * - Single source of truth for logic
 * - Easy to test (mock one hook)
 * - Platform-agnostic template
 */

import React from 'react';
import { ProjectListPageTemplate, showToast } from '@ideoz/ui';
import { useProjectListPageFacade } from '@ideoz/core';
import { useWebNavigation } from '../hooks/useWebNavigation';
import { useAuthDialog } from '../hooks/useAuthDialog';

export default function ProjectListPage(): JSX.Element {
  // ========== Platform-Specific Implementations ==========
  const navigation = useWebNavigation();
  const authDialog = useAuthDialog();
  
  // Adapt authDialog to AuthFacade interface
  const auth = React.useMemo(() => ({
    isOpen: authDialog.isOpen,
    mode: authDialog.mode,
    isLoading: authDialog.isLoading,
    openLogin: authDialog.openLogin,
    openRegister: authDialog.openRegister,
    close: authDialog.closeDialog,
  }), [authDialog]);

  // ========== Shared Facade (100% Reusable) ==========
  const facade = useProjectListPageFacade({
    navigation,
    auth,
    showToast,
  });

  return (
    <ProjectListPageTemplate
      facade={facade}
      
      // Web-specific overrides
      authDialogState={authDialog}
      isWeb={true}
    />
  );
}
